import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import LandingPage from "@/pages/LandingPage";
import AuthCallback from "@/pages/AuthCallback";
import AdminDashboard from "@/pages/AdminDashboard";
import ClientDashboard from "@/pages/ClientDashboard";
import VendorDashboard from "@/pages/VendorDashboard";
import CareersPage from "@/pages/CareersPage";
import TeamPage from "@/pages/TeamPage";
import EmailAuthPage from "@/pages/EmailAuthPage";
import PaymentPage from "@/pages/PaymentPage";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (location.state?.user) {
    return children;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFCFB] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#121212] border-t-transparent animate-spin mx-auto mb-4" />
          <p className="text-xs tracking-[0.2em] uppercase text-[#5C5C5C]">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function DashboardRouter() {
  const { user } = useAuth();
  const location = useLocation();
  const stateUser = location.state?.user;
  const currentUser = user || stateUser;

  if (!currentUser) return <Navigate to="/" replace />;

  const role = currentUser.role;
  if (role === 'admin' || role === 'editor') return <AdminDashboard />;
  if (role === 'vendor' || role === 'contractor' || role === 'subcontractor') return <VendorDashboard />;
  return <ClientDashboard />;
}

function AppRouter() {
  const location = useLocation();

  // CRITICAL: Check URL fragment for session_id synchronously during render
  // This prevents race conditions by processing session_id BEFORE checking existing session
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<EmailAuthPage />} />
      <Route path="/careers" element={<CareersPage />} />
      <Route path="/team" element={<TeamPage />} />
      <Route path="/payment" element={<PaymentPage />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardRouter />
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
